/**
 * Turning capabilities on and off for one tenant.
 *
 * The screen exists to make one distinction visible, because the API makes it
 * and an interface that flattened it would be actively misleading: a flag has
 * **three** states, not two. On, off, and *no decision recorded* — in which
 * case it follows the value the software shipped with, and keeps following it
 * when a later release changes that value.
 *
 * A toggle alone cannot show that. So each row shows the state and, separately,
 * where the state came from, with a third action that removes the decision
 * rather than setting it to the same value.
 *
 * Nothing here authorises anything. The route requires `settings.flag.read`
 * to be reachable and the buttons appear with `settings.flag.manage`, but the
 * server re-authorises every call — see ADR-0008.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { Badge, Button, DataTable, Page, useToast, type Column } from "@hardauto/core-ui";

import { SettingsApi, type Flag } from "../api";
import { NAMESPACE } from "../i18n";
import "./settings.css";

export function FlagsScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new SettingsApi(client), [client]);
  const toast = useToast();

  const [flags, setFlags] = useState<Flag[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Per-key rather than a single boolean: two flags can be changed in quick
  // succession, and a shared flag would disable every row while one saves.
  const [saving, setSaving] = useState<ReadonlySet<string>>(new Set());

  const mayManage = useCan("settings.flag.manage");

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setFlags((await api.listFlags()).flags);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const mutate = useCallback(
    async (flag: Flag, action: () => Promise<unknown>, announce: string) => {
      setSaving((current) => new Set(current).add(flag.key));
      try {
        await action();
        // Re-read rather than patching local state. The server decides what
        // `source` becomes, and guessing it here would put the interface and
        // the truth one release apart the first time that rule changes.
        setFlags((await api.listFlags()).flags);
        toast.show(announce, { tone: "success" });
      } catch {
        toast.show(t("error.save"), { tone: "danger" });
      } finally {
        setSaving((current) => {
          const next = new Set(current);
          next.delete(flag.key);
          return next;
        });
      }
    },
    [api, toast, t],
  );

  const columns: Column<Flag>[] = [
    {
      key: "flag",
      header: t("column.flag"),
      render: (flag) => (
        <div>
          <div className="hardauto-flag__label">{flag.label}</div>
          <div className="hardauto-flag__key">
            <code>{flag.key}</code>
          </div>
          {flag.description ? (
            <p className="hardauto-flag__description">{flag.description}</p>
          ) : null}
        </div>
      ),
    },
    { key: "module", header: t("column.module"), render: (flag) => flag.module },
    {
      key: "state",
      header: t("column.state"),
      render: (flag) => (
        <Badge tone={flag.enabled ? "success" : "neutral"}>
          {t(flag.enabled ? "state.on" : "state.off")}
        </Badge>
      ),
    },
    {
      key: "source",
      header: t("column.source"),
      // The distinction this screen exists for. `neutral` for the default and
      // `info` for a decision, so the two never read as the same thing at a
      // glance — and the title carries what the colour cannot say.
      render: (flag) => (
        <span title={t(flag.source === "tenant" ? "source.tenantHint" : "source.defaultHint")}>
          <Badge tone={flag.source === "tenant" ? "info" : "neutral"}>
            {t(flag.source === "tenant" ? "source.tenant" : "source.default")}
          </Badge>
        </span>
      ),
    },
    {
      key: "actions",
      header: t("column.actions"),
      render: (flag) => {
        if (!mayManage) return null;
        const busy = saving.has(flag.key);
        return (
          <div className="hardauto-flag__actions">
            <Button
              size="sm"
              variant={flag.enabled ? "ghost" : "primary"}
              disabled={busy}
              // Labelled with the flag, not just the verb: a table of
              // identical "Turn off" buttons tells a screen-reader user
              // nothing about which row they are on.
              aria-label={t(flag.enabled ? "action.disableFor" : "action.enableFor", {
                flag: flag.label,
              })}
              onClick={() => {
                void mutate(
                  flag,
                  () => api.setFlag(flag.key, !flag.enabled),
                  t("saved", {
                    flag: flag.label,
                    state: t(flag.enabled ? "state.off" : "state.on").toLowerCase(),
                  }),
                );
              }}
            >
              {t(flag.enabled ? "action.disable" : "action.enable")}
            </Button>

            {/* Only when there is a decision to forget. Offering it on a flag
                that already follows the default would be a control that does
                nothing, which teaches people to distrust the others. */}
            {flag.source === "tenant" ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                aria-label={t("action.clearFor", { flag: flag.label })}
                onClick={() => {
                  void mutate(
                    flag,
                    () => api.clearFlag(flag.key),
                    t("cleared", { flag: flag.label }),
                  );
                }}
              >
                {t("action.clear")}
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <Page title={t("title")} description={t("intro")}>
      {/* Loading, error and empty are the table's own states rather than three
          branches here: rendering them in one place is what makes them
          consistent across every listing in the application. */}
      <DataTable
        caption={t("title")}
        columns={columns}
        rows={flags}
        rowKey={(flag) => flag.key}
        loading={loading}
        loadingLabel={t("loading")}
        error={
          failed
            ? {
                title: t("error.load"),
                onRetry: () => {
                  void load();
                },
                retryLabel: t("error.retry"),
              }
            : undefined
        }
        empty={{ title: t("empty.title"), description: t("empty.body") }}
      />
    </Page>
  );
}
